/**
 * The Peer Interface type catalog + filter vocabularies (ADR-036 §D-1 / §D-4).
 *
 * Pure, React-free. Mirrors the framework's **static** type registry
 * (:file:`packages/meridian_peer_framework/src/meridian_peer_framework/_registry.py`)
 * so the operator picking a type in the management surface sees the same four
 * cells of the ``(Codec, Transport)`` grid the service can actually realize. The
 * registry is not on the OpenAPI surface — the BFF validates ``type_id`` against
 * it server-side but never publishes it — so this is a hand-maintained mirror,
 * and a type added there must be added here too. The consequence of forgetting
 * is bounded and loud: the new type is simply not offered, never mis-offered
 * (the BFF rejects an unregistered ``type_id`` with a 422 regardless).
 *
 * # Why a UI-side catalog at all
 *
 * ``type_id`` alone is not enough to render an honest form. Two facts drive the
 * editor and neither is on the wire:
 *
 *   - **Delivery profile is `mtls-tcp`-only.** ``PeerInterfaceConfig``
 *     rejects a profile on a CDS interface, whose fire-and-forget semantics are
 *     fixed by the accredited CDS contract (ADR-005). Offering the control and
 *     then 422-ing is worse than not offering it.
 *   - **TLS material is `mtls-tcp`-only.** The CDS link is raw TCP; a Consumer CA
 *     bundle on a CDS interface would be inert configuration that reads as a
 *     control that exists.
 *
 * Cross-references:
 *   - ADR-036 §D-1 (interface model), §D-4 (filter), §D-6 (CDS as a type).
 *   - REQ-PEER-029 (registered types); REQ-PEER-032 (filter selectors).
 *   - Storyboard Flow W (docs/engineering/detail/storyboards/flows/W-configure-peer-integration).
 */
import type { components } from "@/lib/api-types.gen";

/** ``mtls-tcp`` delivery semantics; absent on the CDS types (REQ-PEER-049). */
export type DeliveryProfile = components["schemas"]["DeliveryProfile"];
/** Who opens the connection — ``pull`` is the documented default (ADR-017 §8). */
export type ConnectionRole = components["schemas"]["ConnectionRole"];
/** Whether an interface/connection emits (ADR-036 §D-7). */
export type InterfaceState = components["schemas"]["InterfaceState"];
/** Which message-carried position a geo clause tests (OQ-4). */
export type LocationSource = components["schemas"]["LocationSource"];
/** The filter's message-type selector vocabulary. */
export type MessageType = components["schemas"]["MessageType"];

/**
 * One registered interface type, as the operator picks it.
 *
 * ``transportId`` is the discriminator every conditional in the editor keys off —
 * it decides whether delivery profile and TLS material apply — so it is carried
 * verbatim from the registry rather than re-derived from ``typeId``.
 */
export interface InterfaceTypeDescriptor {
  /** Stable public id sent as ``type_id`` (e.g. ``"sapient-cds"``). */
  readonly typeId: string;
  /** Operator-facing label. */
  readonly label: string;
  /** The codec seam this type binds (canonical record → wire bytes). */
  readonly codecId: string;
  /** The transport seam this type binds (bytes → Consumer). */
  readonly transportId: "mtls-tcp" | "cds-xml-tcp";
  /** ``sapient`` or ``raw`` — what the interface carries. */
  readonly payloadDomain: "sapient" | "raw";
  /** One-line operator description (the registry's, verbatim). */
  readonly description: string;
  /**
   * Which way data moves (REQ-PEER-059). A property of the **type**, never of
   * the row — an interface carrying its own direction could contradict its own
   * ``type_id`` and nothing would catch the disagreement.
   *
   * Not to be confused with {@link ConnectionRole}, which says who *opens* the
   * connection. Data flows Meridian → peer under both roles; this says whether
   * it flows that way at all.
   */
  readonly direction: "outbound" | "inbound";
  /**
   * Whether starting this type does anything (REQ-PEER-060).
   *
   * ``false`` marks a type that may be configured and persisted but has no
   * realization — assembly refuses it with an explicit not-implemented error.
   * The UI carries the same reasoning one step earlier: such an interface
   * renders read-only rather than offering controls that would fail at start
   * (REQ-UI-146).
   */
  readonly implemented: boolean;
  /**
   * Whether this type may ONLY run the spooled terminal (REQ-PEER-081).
   *
   * ``true`` for the CDS types: they render to XML and cross a guard that must
   * not hold the ingest cursor, so store-and-forward is a property of the type
   * rather than an operator's choice. The BFF refuses a ``direct`` terminal on
   * such a type with a 422 and assembly refuses to start it, so the UI carries
   * the fact one step earlier — a created interface must not be born into a
   * configuration the server will reject.
   */
  readonly requiresSpool: boolean;
  /**
   * The wire-security modes an interface of this type may run (REQ-PEER-092).
   *
   * Mirrors ``InterfaceTypeDescriptor.allowed_transport_security``. Declared by
   * the **type** because some wires are not an operator's choice: the CDS link
   * is raw TCP by the accredited transfer contract, so its interfaces are
   * plaintext-only and cannot be "hardened" into something that will not start.
   *
   * ⚠ **A mode outside this set is ABSENT from the control, never disabled**
   * (REQ-UI-152) — and the reason for that differs from REQ-UI-143's. A
   * greyed-out `mTLS` on a CDS interface tells an operator they could harden
   * this wire and someone has stopped them; the truth is that nothing can, so a
   * disabled control would be a claim about permissions that is simply false.
   * The constraint is stated instead.
   */
  readonly allowedTransportSecurity: readonly TransportSecurity[];
  /** The mode an interface of this type is born with. Always a member of the set above. */
  readonly defaultTransportSecurity: TransportSecurity;
}

/**
 * How an interface secures its wire (REQ-PEER-091).
 *
 * Re-declared here rather than imported from `peer-model` to keep this module
 * free of the read/write shapes — the catalog is the static registry mirror and
 * nothing else. The generated union is the authority; this alias is structurally
 * identical and `tsc` enforces that at every use site.
 */
export type TransportSecurity = components["schemas"]["TransportSecurity"];

/**
 * The three modes with their operator-facing meaning and consequence.
 *
 * ``consequence`` is non-empty only for ``plaintext``, and is the sentence
 * REQ-UI-152 requires **twice** — at selection and again at confirmation. It is
 * one constant so the two statements cannot drift into saying different things
 * about the same wire.
 */
export const TRANSPORT_SECURITY_MODES: readonly {
  value: TransportSecurity;
  label: string;
  hint: string;
  consequence: string | null;
}[] = [
  {
    value: "plaintext",
    label: "Plaintext — no TLS",
    hint: "No encryption and no authentication on the wire.",
    consequence: "Data to this peer will not be encrypted or authenticated on the wire.",
  },
  {
    value: "tls",
    label: "TLS — peer authenticated",
    hint: "Meridian verifies the peer's chain and presents no certificate of its own.",
    consequence: null,
  },
  {
    value: "mtls",
    label: "mTLS — mutually authenticated",
    hint: "Both ends present and verify certificates. The fail-safe default.",
    consequence: null,
  },
];

/** Look up one mode's presentation, or ``null`` for a token this build does not know. */
export function findTransportSecurity(
  mode: string,
): (typeof TRANSPORT_SECURITY_MODES)[number] | null {
  return TRANSPORT_SECURITY_MODES.find((m) => m.value === mode) ?? null;
}

/**
 * The modes an interface of ``typeId`` may run, in presentation order.
 *
 * An **unknown** type gets the full set rather than an empty one: the interface
 * still lists (never hide a live egress path — CLAUDE.md §8.1), and offering
 * every mode lets the BFF's 422 be the authority rather than this build's
 * ignorance of a type a newer release added.
 */
export function allowedTransportSecurity(typeId: string): readonly TransportSecurity[] {
  const allowed = findInterfaceType(typeId)?.allowedTransportSecurity;
  return TRANSPORT_SECURITY_MODES.filter(
    (m) => allowed === undefined || allowed.includes(m.value),
  ).map((m) => m.value);
}

/**
 * Why a type's permitted set is narrowed, in operator language — or ``null``
 * where it is not narrowed and there is nothing to explain.
 *
 * REQ-UI-152 requires the reason to be *stated* wherever a mode is absent. An
 * absence with no cause given is indistinguishable from a broken control.
 */
export function transportSecurityFixedReason(typeId: string): string | null {
  const descriptor = findInterfaceType(typeId);
  if (descriptor === null || descriptor.allowedTransportSecurity.length === 3) return null;
  if (descriptor.transportId === "cds-xml-tcp") {
    return "The CDS link is raw TCP by the accredited transfer contract, so this interface type cannot run TLS or mTLS.";
  }
  if (descriptor.direction === "inbound") {
    return "An inbound tasking channel is mutually authenticated by definition; no weaker mode is offered.";
  }
  // Reached only by a type whose set is narrowed for a reason this build has no
  // sentence for. Saying so is better than saying nothing — the operator learns
  // the constraint is real and that the UI cannot explain it, which is true.
  return "This interface type permits only the mode shown.";
}

/**
 * The four initial interface types (ADR-036 §D-1), in the order the picker
 * offers them: the two direct-Consumer types first, then the two CDS types,
 * because a deployment's CDS interfaces normally arrive by seed (§D-5) and are
 * far less often authored by hand.
 */
export const INTERFACE_TYPES: readonly InterfaceTypeDescriptor[] = [
  {
    direction: "outbound",
    implemented: true,
    requiresSpool: false,
    allowedTransportSecurity: ["plaintext", "tls", "mtls"],
    defaultTransportSecurity: "mtls",
    typeId: "sapient-data",
    label: "SAPIENT data",
    codecId: "sapient-native",
    transportId: "mtls-tcp",
    payloadDomain: "sapient",
    description: "Canonical SapientMessage over direct bidirectional-mTLS TCP to a Consumer.",
  },
  {
    direction: "outbound",
    implemented: true,
    requiresSpool: false,
    allowedTransportSecurity: ["plaintext", "tls", "mtls"],
    defaultTransportSecurity: "mtls",
    typeId: "raw-data",
    label: "Raw data",
    codecId: "raw-native",
    transportId: "mtls-tcp",
    payloadDomain: "raw",
    description: "Raw sensor blob (fetched from the Raw Data Store) over direct mTLS TCP.",
  },
  {
    direction: "outbound",
    implemented: true,
    requiresSpool: true,
    allowedTransportSecurity: ["plaintext"],
    defaultTransportSecurity: "plaintext",
    typeId: "sapient-cds",
    label: "SAPIENT CDS",
    codecId: "sapient-xml",
    transportId: "cds-xml-tcp",
    payloadDomain: "sapient",
    description: "Meridian-SAPIENT-XML TransferEnvelope over the CDS raw-TCP link.",
  },
  {
    direction: "outbound",
    implemented: true,
    requiresSpool: true,
    allowedTransportSecurity: ["plaintext"],
    defaultTransportSecurity: "plaintext",
    typeId: "raw-cds",
    label: "Raw CDS",
    codecId: "raw-xml",
    transportId: "cds-xml-tcp",
    payloadDomain: "raw",
    description: "Raw-blob RawPayload chunk XML over the CDS raw-TCP link.",
  },
  // Reserved, not built (REQ-PEER-060; ADR-039 §D-3). Mirrored from the registry
  // rather than omitted, for the registry's own reason: an operator who
  // configures one gets an explicit "not implemented" instead of "unknown type",
  // which reads as a typo and invites a retry. Listed last because it is the one
  // type that cannot be started.
  {
    direction: "inbound",
    implemented: false,
    requiresSpool: false,
    allowedTransportSecurity: ["mtls"],
    defaultTransportSecurity: "mtls",
    typeId: "tasking-input",
    label: "Tasking input (reserved)",
    codecId: "sapient-native",
    transportId: "mtls-tcp",
    payloadDomain: "sapient",
    description: "RESERVED — inbound peer tasking. Configurable but not implemented.",
  },
];

/**
 * Look up a type descriptor, or ``null`` for one this build does not know.
 *
 * Null rather than a throw or a fabricated placeholder: an interface persisted
 * by a newer release must still be **listed** (its name and state are exactly
 * what an operator needs to stop it), so the surface degrades to showing the raw
 * ``type_id`` rather than hiding the row — CLAUDE.md §8.1, never hide a live
 * egress path.
 */
export function findInterfaceType(typeId: string): InterfaceTypeDescriptor | null {
  return INTERFACE_TYPES.find((descriptor) => descriptor.typeId === typeId) ?? null;
}

/**
 * Whether an interface of this type may be edited and started at all.
 *
 * ``true`` for a type whose descriptor declares an **inbound** direction or
 * which has no realization — today that is exactly ``tasking-input``
 * (REQ-UI-146). Keyed on the descriptor's declared flags rather than on the
 * ``type_id`` string, so a second reserved type needs no change here.
 *
 * An **unknown** type counts as reserved too: this build cannot know what its
 * fields mean, and offering an editor over a document it does not understand
 * risks saving back a lossy version of a live egress path. It still *lists* —
 * never hide a running flow (CLAUDE.md §8.1).
 */
export function isReservedType(typeId: string): boolean {
  const descriptor = findInterfaceType(typeId);
  return descriptor === null || descriptor.direction === "inbound" || !descriptor.implemented;
}

/**
 * The types an operator may pick when **creating** an interface.
 *
 * Excludes the reserved ones: offering a type at create time that cannot then be
 * started would be an affordance leading nowhere. An interface already
 * configured with a reserved type still renders (read-only) — an existing one is
 * a fact about the deployment, and hiding it would be worse.
 */
export const CREATABLE_INTERFACE_TYPES: readonly InterfaceTypeDescriptor[] = INTERFACE_TYPES.filter(
  (descriptor) => !isReservedType(descriptor.typeId),
);

/**
 * Whether this type's delivery semantics are operator-selectable
 * (``mtls-tcp`` only — ``PeerInterfaceConfig._coherent_interface`` rejects a
 * profile on any other transport).
 */
export function supportsDeliveryProfile(typeId: string): boolean {
  return findInterfaceType(typeId)?.transportId === "mtls-tcp";
}

/**
 * The terminal a new interface of ``typeId`` should be born with (REQ-PEER-079).
 *
 * Mirrors the registry invariant the server-side test pins: a type that requires
 * a spool defaults to ``spooled``, everything else to the fail-safe ``direct``.
 * An unknown type gets ``direct`` — the BFF rejects the ``type_id`` itself with a
 * 422, and guessing ``spooled`` for something unrecognized would be the less
 * conservative reading.
 */
export function defaultTerminalFor(typeId: string): "direct" | "spooled" {
  return findInterfaceType(typeId)?.requiresSpool ? "spooled" : "direct";
}

// ⚠ `supportsTlsMaterial(typeId)` was retired 2026-08-17 (REQ-UI-152). It asked
// whether a TYPE carries per-interface TLS material, and the editor keyed the
// trust section on it. That is no longer the question: trust material is
// disclosed by the selected `transport_security` (feature-spec §5.4) — a direct
// type set to `plaintext` presents and verifies nothing, and the type-level
// answer would have shown it three cert fields that configure nothing. Removed
// rather than left in place, because a predicate whose answer no longer governs
// anything is how dead configuration survives a refactor and reads as a control.

/** The two ``mtls-tcp`` delivery profiles with their operator-facing meaning. */
export const DELIVERY_PROFILES: readonly { value: DeliveryProfile; label: string; hint: string }[] =
  [
    {
      value: "framed_ack",
      label: "Framed ACK (at-least-once)",
      hint: "The Consumer echoes a body-SHA-256 ACK; bounded retry, then quarantine.",
    },
    {
      value: "write_drain",
      label: "Write-drain (fire-and-forget)",
      hint: "A successful framed write counts as delivered. No delivery evidence.",
    },
  ];

/** Connection directions with their operator-facing meaning (OQ-5). */
export const CONNECTION_DIRECTIONS: readonly {
  value: ConnectionRole;
  label: string;
  hint: string;
}[] = [
  {
    value: "pull",
    label: "Consumer pulls",
    hint: "Meridian binds the port and the Consumer connects. The documented default.",
  },
  {
    value: "dial_out",
    label: "Meridian dials out",
    hint: "Meridian opens the connection to the Consumer's host and port.",
  },
];

/** The filter's ``message_type`` selector vocabulary (REQ-PEER-032). */
export const MESSAGE_TYPES: readonly MessageType[] = [
  "status",
  "detection",
  "alert",
  "registration",
  "raw",
];

/**
 * The geo clause's location sources (OQ-4). Each names a position carried **on
 * the message itself**; a record that does not carry it never matches, which is
 * the egress mirror of the REQ-INGEST-044 "never substitute (0,0)" discipline
 * and is stated in the hint because it is the one surprising thing about geo
 * filtering here.
 */
export const LOCATION_SOURCES: readonly { value: LocationSource; label: string; hint: string }[] = [
  {
    value: "sensor_location",
    label: "Sensor location",
    hint: "The reporting platform's own position — carried only on a Status message.",
  },
  {
    value: "detection_location",
    label: "Detection location",
    hint: "Where a detection was observed. Only Detections carry it.",
  },
  {
    value: "alert_location",
    label: "Alert location",
    hint: "Where an alert was raised. Only Alerts carry it.",
  },
  {
    value: "capture_location",
    label: "Capture location",
    hint: "Where a raw blob was captured. Only raw records carry it.",
  },
];

/**
 * BSI ``Alert.AlertType`` enum names as the egress filter sees them.
 *
 * The fact extractor projects ``alert.alert_type.name`` — the **short** betterproto2
 * name (``"CRITICAL"``, not ``"ALERT_TYPE_CRITICAL"``), asserted by
 * ``test_alert_type_and_priority_project_to_enum_names``. Matching is exact and
 * un-normalized, so offering the closed list rather than a free-text box removes
 * the entire class of filters that silently match nothing.
 */
export const ALERT_TYPES: readonly string[] = [
  "INFORMATION",
  "WARNING",
  "CRITICAL",
  "ERROR",
  "FATAL",
  "MODE_CHANGE",
];

/** BSI ``Alert.DiscretePriority`` enum names, same short-name projection as {@link ALERT_TYPES}. */
export const ALERT_PRIORITIES: readonly string[] = ["LOW", "MEDIUM", "HIGH"];
